import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../core/services/auth.service';
import { catalogImageAbsoluteUrl } from '../../shared/utils/catalog-image-url';
import type { WishlistDto, WishlistItemDto, WishlistSnapshot } from './wishlist.model';

const GUEST_KEY = 'hon_wishlist';

@Injectable({ providedIn: 'root' })
export class WishlistService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly apiUrl = `${environment.apiUrl}CustomerAccount`;
  private readonly imageBaseUrl = environment.imageBaseUrl;
  private syncing = false;

  readonly items = signal<WishlistItemDto[]>([]);
  readonly catalogIds = computed(() => new Set(this.items().map((item) => item.productCatalogId)));
  readonly loaded = signal(false);

  isSaved(catalogId: number): boolean {
    return this.catalogIds().has(catalogId);
  }

  bootstrap(): void {
    if (!isPlatformBrowser(this.platformId) || this.loaded()) {
      return;
    }

    if (this.usesApi()) {
      void this.onAuthenticated();
      return;
    }

    this.items.set(this.readGuest());
    this.loaded.set(true);
  }

  async onAuthenticated(): Promise<void> {
    if (!isPlatformBrowser(this.platformId) || this.syncing) {
      return;
    }

    this.syncing = true;
    try {
      const guest = this.readGuest();
      if (guest.length > 0 && this.usesApi()) {
        await firstValueFrom(
          this.http.post(`${this.apiUrl}/MergeWishlist`, {
            productCatalogIds: guest.map((item) => item.productCatalogId),
          }),
        );
        this.clearGuest();
      }

      if (this.usesApi()) {
        await this.refreshFromServer();
      } else {
        this.items.set(guest);
      }
      this.loaded.set(true);
    } catch {
      this.items.set(this.readGuest());
      this.loaded.set(true);
    } finally {
      this.syncing = false;
    }
  }

  async toggle(snapshot: WishlistSnapshot): Promise<void> {
    if (this.isSaved(snapshot.catalogId)) {
      await this.remove(snapshot.catalogId);
      return;
    }
    await this.add(snapshot);
  }

  async add(snapshot: WishlistSnapshot): Promise<void> {
    const next = [this.toItem(snapshot), ...this.items().filter((item) => item.productCatalogId !== snapshot.catalogId)];
    this.items.set(next);

    if (!this.usesApi()) {
      this.writeGuest(next.map((item) => this.toSnapshot(item)));
      return;
    }

    try {
      await firstValueFrom(this.http.post<number>(`${this.apiUrl}/AddWishlistItem`, { productCatalogId: snapshot.catalogId }));
    } catch {
      this.items.update((items) => items.filter((item) => item.productCatalogId !== snapshot.catalogId));
    }
  }

  async remove(catalogId: number): Promise<void> {
    const previous = this.items();
    this.items.set(previous.filter((item) => item.productCatalogId !== catalogId));

    if (!this.usesApi()) {
      this.writeGuest(this.items().map((item) => this.toSnapshot(item)));
      return;
    }

    try {
      await firstValueFrom(
        this.http.delete(`${this.apiUrl}/RemoveWishlistItem`, { params: { productCatalogId: catalogId } }),
      );
    } catch {
      this.items.set(previous);
    }
  }

  imageSrc(path: string | null | undefined): string {
    return catalogImageAbsoluteUrl(this.imageBaseUrl, path) || 'assets/images/logo.png';
  }

  private async refreshFromServer(): Promise<void> {
    const response = await firstValueFrom(this.http.get<WishlistDto>(`${this.apiUrl}/GetWishlist`));
    this.items.set(response.items ?? []);
  }

  private usesApi(): boolean {
    return this.auth.isAuthenticated() && !environment.devLoginBypass;
  }

  private toItem(snapshot: WishlistSnapshot): WishlistItemDto {
    return {
      id: 0,
      productCatalogId: snapshot.catalogId,
      name: snapshot.name,
      slug: snapshot.slug,
      imageUrl: snapshot.imageUrl,
      unitPrice: snapshot.unitPrice,
      variantId: snapshot.variantId,
      variantName: snapshot.variantName,
      reviewCount: 0,
    };
  }

  private toSnapshot(item: WishlistItemDto): WishlistSnapshot {
    return {
      catalogId: item.productCatalogId,
      slug: item.slug,
      name: item.name,
      imageUrl: item.imageUrl,
      unitPrice: item.unitPrice,
      variantId: item.variantId,
      variantName: item.variantName,
    };
  }

  private readGuest(): WishlistItemDto[] {
    try {
      const raw = localStorage.getItem(GUEST_KEY);
      if (!raw) {
        return [];
      }
      const parsed = JSON.parse(raw) as WishlistSnapshot[];
      if (!Array.isArray(parsed)) {
        return [];
      }
      return parsed
        .filter((item) => item && Number.isFinite(item.catalogId))
        .map((item) => this.toItem(item));
    } catch {
      return [];
    }
  }

  private writeGuest(items: WishlistSnapshot[]): void {
    localStorage.setItem(GUEST_KEY, JSON.stringify(items));
  }

  private clearGuest(): void {
    localStorage.removeItem(GUEST_KEY);
  }

  clearOnLogout(): void {
    this.items.set(this.readGuest());
    this.loaded.set(true);
  }
}
